import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CrmLayout from "@/components/feature/CrmLayout";
import { useToast } from "@/components/base/Toast";
import { fetchApi } from "@/services/apiService";
import { allAgents, allStages, allSources } from "@/mocks/leadsData";

type Step = 1 | 2 | 3;

interface MappedColumn {
  fileColumn: string;
  previewValue: string;
  crmField: string;
  autoMatched: boolean;
}

const sampleFileColumns: MappedColumn[] = [
  { fileColumn: "Full Name", previewValue: "Vikram Mehta", crmField: "Lead Name", autoMatched: true },
  { fileColumn: "Mobile No", previewValue: "9876543210", crmField: "Phone", autoMatched: true },
  { fileColumn: "Email ID", previewValue: "vikram@example.com", crmField: "Email", autoMatched: true },
  { fileColumn: "Company", previewValue: "TechVentures Inc", crmField: "Company Name", autoMatched: true },
  { fileColumn: "Source", previewValue: "IndiaMart", crmField: "Lead Source", autoMatched: false },
  { fileColumn: "Budget", previewValue: "₹24,500", crmField: "Deal Value", autoMatched: false },
  { fileColumn: "Remarks", previewValue: "Interested in 2BHK", crmField: "Notes", autoMatched: false },
  { fileColumn: "Status", previewValue: "New", crmField: "Do not import", autoMatched: false },
];

const crmFields = [
  "Lead Name", "Phone", "Email", "Company Name",
  "Lead Source", "Stage", "Priority", "Deal Value",
  "Notes", "Do not import",
];

const previewRows = [
  ["Vikram Mehta", "9876543210", "vikram@example.com", "TechVentures Inc", "IndiaMart", "₹24,500", "Interested in 2BHK", "New"],
  ["Anita Desai", "9845123456", "anita@example.com", "GreenLeaf Organics", "JustDial", "₹12,800", "Looking for 3BHK", "New"],
  ["Suresh Nair", "9712345678", "suresh@example.com", "Skyline Architects", "Website", "₹45,200", "Architect firm inquiry", "New"],
];

const stepLabels = ["Upload File", "Map Columns", "Review & Import"];

export default function ImportLeadsPage() {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>(1);
  const [fileSelected, setFileSelected] = useState(false);
  const [fileName, setFileName] = useState("");
  const [columns, setColumns] = useState<MappedColumn[]>(sampleFileColumns);
  const [duplicateHandling, setDuplicateHandling] = useState<"skip" | "update" | "import">("skip");
  const [assignTo, setAssignTo] = useState<string>(allAgents[0]);
  const [useAssignmentRules, setUseAssignmentRules] = useState(false);
  const [defaultStage, setDefaultStage] = useState("New Inquiry");
  const [defaultSource, setDefaultSource] = useState("Manual");
  const [showErrors, setShowErrors] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importComplete, setImportComplete] = useState(false);
  const [importProgress, setImportProgress] = useState(0);

  const [dbStages, setDbStages] = useState<string[]>([]);
  const [dbSources, setDbSources] = useState<string[]>([]);
  const [dbAgents, setDbAgents] = useState<string[]>([]);

  useEffect(() => {
    fetchApi<any[]>('/config/pipeline-stages/get-all', undefined, [])
      .then((res) => {
        const items = Array.isArray(res) ? res : (res as any)?.data || [];
        if (items.length > 0) {
          const names = items.map((s: any) => s.name).filter(Boolean);
          if (names.length > 0) {
            setDbStages(names);
            setDefaultStage(names[0]);
          }
        }
      })
      .catch(() => {});

    fetchApi<any[]>('/config/lead-sources/get-all', undefined, [])
      .then((res) => {
        const items = Array.isArray(res) ? res : (res as any)?.data || [];
        if (items.length > 0) {
          const names = items.map((s: any) => s.name).filter(Boolean);
          if (names.length > 0) {
            setDbSources(names);
            setDefaultSource(names[0]);
          }
        }
      })
      .catch(() => {});

    fetchApi<any>('/auth/company-user/all', undefined, null)
      .then((res) => {
        const userList = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data?.items)
          ? res.data.items
          : Array.isArray(res?.data)
          ? res.data
          : [];

        const agentNames: string[] = [];
        userList.forEach((t: any) => {
          if (t.name) agentNames.push(t.name);
          else if (t.fullName) agentNames.push(t.fullName);
          else if (t.username) agentNames.push(t.username);
          if (t.leader_name) agentNames.push(t.leader_name);
          if (Array.isArray(t.member_names)) agentNames.push(...t.member_names);
        });
        const unique = Array.from(new Set(agentNames)).filter(Boolean);
        if (unique.length > 0) {
          setDbAgents(unique);
          setAssignTo(unique[0]);
        }
      })
      .catch(() => {});
  }, []);

  const stageOptions = dbStages.length > 0 ? dbStages : allStages;
  const sourceOptions = dbSources.length > 0 ? dbSources : allSources;
  const agentOptions = dbAgents.length > 0 ? dbAgents : allAgents;

  const autoMatchedCount = columns.filter((c) => c.autoMatched).length;
  const unmatchedCount = columns.filter((c) => !c.autoMatched && c.crmField === "Do not import").length;
  const needsMapping = columns.length - autoMatchedCount - unmatchedCount;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
      setFileSelected(true);
    }
  };

  const handleBrowse = () => {
    fileInputRef.current?.click();
  };

  const handleUpdateColumn = (index: number, field: string) => {
    const updated = [...columns];
    updated[index] = { ...updated[index], crmField: field };
    setColumns(updated);
  };

  const handleImport = async () => {
    setImporting(true);
    setImportProgress(0);

    const sampleLeads = [
      { name: "Vikram Mehta", phone: "9876543210", email: "vikram@example.com", company: "TechVentures Inc", source: defaultSource || "IndiaMart", value: "₹24,500", notes: "Interested in 2BHK", stage: defaultStage, assigned: useAssignmentRules ? "Rajesh Kumar" : assignTo },
      { name: "Anita Desai", phone: "9845123456", email: "anita@example.com", company: "GreenLeaf Organics", source: defaultSource || "JustDial", value: "₹12,800", notes: "Looking for 3BHK", stage: defaultStage, assigned: useAssignmentRules ? "Priya Sharma" : assignTo },
      { name: "Suresh Nair", phone: "9712345678", email: "suresh@example.com", company: "Skyline Architects", source: defaultSource || "Website", value: "₹45,200", notes: "Architect firm inquiry", stage: defaultStage, assigned: useAssignmentRules ? "Amit Patel" : assignTo },
    ];

    try {
      for (let i = 0; i < sampleLeads.length; i++) {
        await fetchApi('/leads', {
          method: 'POST',
          body: JSON.stringify(sampleLeads[i]),
        });
        setImportProgress(i + 1);
      }
      setImportProgress(sampleLeads.length);
      setImporting(false);
      setImportComplete(true);
      showToast("Leads imported & saved to database successfully!", "success");
    } catch (err: any) {
      showToast(err.message || "Failed to import leads", "error");
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const csv = "Full Name,Mobile No,Email ID,Company,Source,Budget,Remarks,Status\nVikram Mehta,9876543210,vikram@example.com,TechVentures Inc,IndiaMart,24500,Interested in 2BHK,New\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_leads_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const progressPercent = step === 1 ? 33 : step === 2 ? 66 : 100;

  return (
    <CrmLayout title="Import Leads">
      <div className="p-5 md:p-6 space-y-5">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Import Leads</h1>
          <p className="text-sm text-gray-500 mt-0.5">Bulk import leads from CSV or Excel file</p>
        </div>

        {/* Step indicator */}
        <div>
          <div className="flex items-center justify-between max-w-lg mx-auto">
            {stepLabels.map((label, i) => (
              <div key={label} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                  i + 1 <= step ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-400"
                }`}>
                  {i + 1}
                </div>
                <span className={`text-sm font-medium hidden sm:block whitespace-nowrap ${
                  i + 1 <= step ? "text-gray-900" : "text-gray-400"
                }`}>{label}</span>
                {i < 2 && (
                  <div className={`hidden sm:block w-12 md:w-20 h-0.5 mx-2 ${
                    i + 1 < step ? "bg-indigo-600" : "bg-gray-200"
                  }`} />
                )}
              </div>
            ))}
          </div>
          {/* Progress bar */}
          <div className="mt-4 max-w-lg mx-auto bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-indigo-600 h-1.5 rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* ──────────────── STEP 1: UPLOAD ──────────────── */}
        {step === 1 && (
          <div className="bg-white rounded-xl border border-gray-200 p-8">
            {!fileSelected ? (
              <>
                {/* Dropzone */}
                <div
                  onClick={handleBrowse}
                  className="border-2 border-dashed border-indigo-200 rounded-2xl p-16 text-center bg-indigo-50/30 hover:bg-indigo-50 cursor-pointer transition-colors"
                >
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-indigo-100 flex items-center justify-center">
                    <i className="ri-upload-cloud-2-line text-3xl text-indigo-400 w-8 h-8 flex items-center justify-center" />
                  </div>
                  <p className="text-lg text-gray-700 mb-1">Drop your file here or <span className="text-indigo-600 underline cursor-pointer">browse</span></p>
                  <p className="text-sm text-gray-400">Accepted: .csv, .xlsx, .xls</p>
                  <p className="text-xs text-gray-400 mt-1">Maximum file size: 5MB</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>

                {/* Template download */}
                <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-amber-600 text-sm w-4 h-4 flex items-center justify-center" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-amber-800">Don't have a template?</p>
                    <p className="text-sm text-amber-700 mt-0.5">Download our sample CSV template to ensure your data is formatted correctly.</p>
                  </div>
                  <button
                    onClick={handleDownloadTemplate}
                    className="px-4 py-2 rounded-lg border border-amber-600 text-amber-600 text-sm font-medium hover:bg-amber-100 transition-colors whitespace-nowrap flex-shrink-0"
                  >
                    Download Template
                  </button>
                </div>
              </>
            ) : (
              /* File selected */
              <div className="text-center py-10">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-green-100 flex items-center justify-center">
                  <i className="ri-file-excel-2-line text-3xl text-green-600 w-8 h-8 flex items-center justify-center" />
                </div>
                <p className="text-lg font-semibold text-gray-900">{fileName}</p>
                <p className="text-sm text-gray-500">24 KB</p>
                <div className="flex items-center justify-center gap-2 mt-3">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                    <i className="ri-check-line text-green-600 text-xs w-3 h-3 flex items-center justify-center" />
                  </div>
                  <span className="text-sm text-green-600 font-medium">File ready to process</span>
                </div>
                <button
                  onClick={() => { setFileSelected(false); setFileName(""); }}
                  className="text-xs text-indigo-600 hover:text-indigo-700 font-medium mt-3"
                >
                  Change file
                </button>
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setStep(2)}
                disabled={!fileSelected}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${
                  fileSelected
                    ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                Next: Map Columns →
              </button>
            </div>
          </div>
        )}

        {/* ──────────────── STEP 2: MAP COLUMNS ──────────────── */}
        {step === 2 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-base font-bold text-gray-900">Map your columns to CRM fields</h3>
                <p className="text-sm text-gray-500 mt-0.5">We detected 8 columns in your file. Match each to the correct CRM field.</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium whitespace-nowrap">
                  {autoMatchedCount} of {columns.length} auto-matched
                </span>
                {needsMapping > 0 && (
                  <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-medium whitespace-nowrap">
                    {needsMapping} need manual mapping
                  </span>
                )}
              </div>
            </div>

            {/* Mapping table header */}
            <div className="grid grid-cols-2 gap-4 px-4 py-2 bg-gray-50 rounded-t-lg border-b border-gray-200">
              <span className="text-xs uppercase font-semibold text-gray-400">Your File Column</span>
              <span className="text-xs uppercase font-semibold text-gray-400">CRM Field</span>
            </div>

            {/* Mapping rows */}
            <div className="border border-gray-200 rounded-b-lg overflow-hidden">
              {columns.map((col, idx) => (
                <div
                  key={col.fileColumn}
                  className={`grid grid-cols-2 gap-4 px-4 py-3 border-b border-gray-100 last:border-b-0 ${
                    col.autoMatched ? "bg-green-50/30" : col.crmField === "Do not import" ? "bg-red-50/30" : ""
                  }`}
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">{col.fileColumn}</p>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">{col.previewValue}</p>
                  </div>
                  <div>
                    <select
                      value={col.crmField}
                      onChange={(e) => handleUpdateColumn(idx, e.target.value)}
                      className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-300 ${
                        col.autoMatched
                          ? "border-green-200 bg-green-50 text-green-700"
                          : col.crmField === "Do not import"
                          ? "border-red-200 bg-red-50 text-red-600"
                          : "border-amber-200 bg-white text-gray-900"
                      }`}
                    >
                      {crmFields.map((f) => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>

            {/* Preview */}
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Preview (first 3 rows of your data)</h4>
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      {columns.filter((c) => c.crmField !== "Do not import").map((c) => (
                        <th key={c.fileColumn} className="px-3 py-2 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                          {c.crmField}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, rIdx) => (
                      <tr key={rIdx} className="border-t border-gray-100">
                        {row.filter((_, cIdx) => columns[cIdx]?.crmField !== "Do not import").map((cell, cIdx) => (
                          <td key={cIdx} className="px-3 py-2 text-xs text-gray-700 whitespace-nowrap">{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between mt-6">
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                ← Back
              </button>
              <button
                onClick={() => setStep(3)}
                className="px-5 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors whitespace-nowrap"
              >
                Next: Review →
              </button>
            </div>
          </div>
        )}

        {/* ──────────────── STEP 3: REVIEW & IMPORT ──────────────── */}
        {step === 3 && !importComplete && !importing && (
          <div className="space-y-5">
            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-green-100 flex items-center justify-center">
                  <i className="ri-check-line text-green-600 text-sm w-4 h-4 flex items-center justify-center" />
                </div>
                <p className="text-2xl font-bold text-green-700">247</p>
                <p className="text-sm text-green-600">Ready to import</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-amber-100 flex items-center justify-center">
                  <i className="ri-error-warning-line text-amber-600 text-sm w-4 h-4 flex items-center justify-center" />
                </div>
                <p className="text-2xl font-bold text-amber-700">12</p>
                <p className="text-sm text-amber-600">Duplicates detected</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-red-100 flex items-center justify-center">
                  <i className="ri-close-line text-red-600 text-sm w-4 h-4 flex items-center justify-center" />
                </div>
                <p className="text-2xl font-bold text-red-700">3</p>
                <p className="text-sm text-red-600">Errors found</p>
              </div>
            </div>

            {/* Duplicate handling */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">How to handle duplicates?</h4>
              <div className="space-y-3">
                {[
                  { value: "skip" as const, label: "Skip duplicates", desc: "Don't import leads with matching phone numbers", rec: true },
                  { value: "update" as const, label: "Update existing leads", desc: "Update fields if phone matches existing lead", rec: false },
                  { value: "import" as const, label: "Import anyway", desc: "Create new lead even if phone exists", rec: false },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      duplicateHandling === opt.value
                        ? "border-indigo-300 bg-indigo-50"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="duplicate"
                      value={opt.value}
                      checked={duplicateHandling === opt.value}
                      onChange={() => setDuplicateHandling(opt.value)}
                      className="mt-0.5 accent-indigo-600"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {opt.label}
                        {opt.rec && <span className="ml-2 px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-[10px] rounded-full font-medium">Recommended</span>}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Error details */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <button
                onClick={() => setShowErrors(!showErrors)}
                className="flex items-center gap-2 text-sm font-medium text-red-600 hover:text-red-700 transition-colors"
              >
                {showErrors ? <i className="ri-arrow-up-s-line w-4 h-4 flex items-center justify-center" /> : <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />}
                3 rows have errors — click to view
              </button>
              {showErrors && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">
                    <i className="ri-error-warning-line text-red-500 flex-shrink-0 mt-0.5 w-4 h-4 flex items-center justify-center" />
                    <span>Row 5: Missing phone number</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">
                    <i className="ri-error-warning-line text-red-500 flex-shrink-0 mt-0.5 w-4 h-4 flex items-center justify-center" />
                    <span>Row 23: Invalid email format</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">
                    <i className="ri-error-warning-line text-red-500 flex-shrink-0 mt-0.5 w-4 h-4 flex items-center justify-center" />
                    <span>Row 89: Missing lead name</span>
                  </div>
                </div>
              )}
            </div>

            {/* Import options */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Import Options</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Assign all imported leads to:</label>
                  <div className="flex items-center gap-4">
                    <select
                      value={assignTo}
                      onChange={(e) => setAssignTo(e.target.value)}
                      disabled={useAssignmentRules}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-300 disabled:opacity-50"
                    >
                      {agentOptions.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useAssignmentRules}
                        onChange={(e) => setUseAssignmentRules(e.target.checked)}
                        className="accent-indigo-600"
                      />
                      Use assignment rules
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Default stage:</label>
                    <select
                      value={defaultStage}
                      onChange={(e) => setDefaultStage(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                    >
                      {stageOptions.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Default source if not in file:</label>
                    <select
                      value={defaultSource}
                      onChange={(e) => setDefaultSource(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                    >
                      {sourceOptions.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => setStep(2)}
                className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                ← Back
              </button>
              <button
                onClick={handleImport}
                className="px-6 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-base transition-colors whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-upload-2-line w-5 h-5 flex items-center justify-center" />
                Import 247 Leads
              </button>
            </div>
          </div>
        )}

        {/* ──────────────── IMPORTING STATE ──────────────── */}
        {importing && (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-indigo-100 flex items-center justify-center">
              <i className="ri-loader-4-line text-3xl text-indigo-600 w-8 h-8 flex items-center justify-center animate-spin" />
            </div>
            <p className="text-lg font-semibold text-gray-900 mb-2">Importing leads...</p>
            <div className="max-w-xs mx-auto bg-gray-200 rounded-full h-2 mb-3">
              <div
                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.round((importProgress / 247) * 100)}%` }}
              />
            </div>
            <p className="text-sm text-gray-500">{importProgress} / 247 imported</p>
            <p className="text-xs text-gray-400 mt-1">~30 seconds remaining</p>
          </div>
        )}

        {/* ──────────────── IMPORT COMPLETE ──────────────── */}
        {importComplete && (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <i className="ri-check-line text-4xl text-green-600 w-10 h-10 flex items-center justify-center" />
            </div>
            <p className="text-2xl font-bold text-gray-900 mb-1">Import Complete!</p>
            <p className="text-lg text-gray-600 mb-1">247 leads imported successfully</p>
            <p className="text-sm text-gray-400">12 duplicates skipped · 3 errors skipped</p>
            <button
              onClick={() => navigate("/leads")}
              className="mt-6 px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm transition-colors whitespace-nowrap"
            >
              View all imported leads
            </button>
          </div>
        )}
      </div>
    </CrmLayout>
  );
}